import { z } from 'zod';
import { articleUnitSchema, sourcingSchema } from '../inventory/article.schema';
import { createCommodityGroupSchema } from './commodity-group.schema';
import { createMaterialSchema } from './material.schema';

/**
 * Bulk-Import (Modul engineering, ADR 0031): einheitliches Ergebnis-Format für die
 * Stammdaten- und Artikelnummern-Importe. `created`/`skipped` (bereits vorhanden) +
 * pro fehlerhafter Zeile ein Eintrag mit Original-Wert und Begründung.
 */
export const importErrorSchema = z.object({
  index: z.number().int(),
  value: z.string(),
  message: z.string(),
});
export type ImportError = z.infer<typeof importErrorSchema>;

export const bulkImportResultSchema = z.object({
  created: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  errors: z.array(importErrorSchema),
});
export type BulkImportResult = z.infer<typeof bulkImportResultSchema>;

/** Obergrenze je Import-Aufruf — schützt vor versehentlichen Riesen-Uploads. */
const MAX_IMPORT_ROWS = 5000;

/** Warengruppen importieren (CSV: code, name, [mainGroupCode]). */
export const importCommodityGroupsSchema = z.object({
  rows: z.array(createCommodityGroupSchema).min(1).max(MAX_IMPORT_ROWS),
});
export type ImportCommodityGroupsInput = z.infer<typeof importCommodityGroupsSchema>;

/** Materialcodes importieren (CSV: code, name). */
export const importMaterialsSchema = z.object({
  rows: z.array(createMaterialSchema).min(1).max(MAX_IMPORT_ROWS),
});
export type ImportMaterialsInput = z.infer<typeof importMaterialsSchema>;

/**
 * Eine Zeile des Artikelnummern-Imports: die fertige Artikelnummer plus die
 * Mindest-Stammdaten für den echten Artikel. Zeichnungsnummer, Warengruppe und
 * Material werden serverseitig aus der Nummer abgeleitet.
 */
export const importArticleNumberRowSchema = z.object({
  articleNumber: z
    .string()
    .trim()
    .regex(/^\d{4}\.\d{5}\.\d{3}$/, 'Artikelnummer muss dem Format XXXX.YYYYY.ZZZ entsprechen.'),
  name: z.string().trim().min(1).max(200),
  nameEn: z.string().trim().max(200).optional(),
  description: z.string().trim().max(2000).optional(),
  unit: articleUnitSchema.optional(),
  sourcing: sourcingSchema.optional(),
});
export type ImportArticleNumberRow = z.infer<typeof importArticleNumberRowSchema>;

export const importArticleNumbersSchema = z.object({
  rows: z.array(importArticleNumberRowSchema).min(1).max(MAX_IMPORT_ROWS),
});
export type ImportArticleNumbersInput = z.infer<typeof importArticleNumbersSchema>;
